/**
 * Non-blocking download progress toast for auto-updates.
 *
 * Shows a persistent indicator when an update is downloading,
 * with progress bar and bytes transferred information.
 *
 * OWNERSHIP: this toast covers the 'available'/'downloading' states ONLY.
 * The 'downloaded' state belongs exclusively to UpdateReadyPrompt (via
 * UpdateReadyToast) — both components used to render their own "Update
 * ready" toast, stacking two prompts. UpdateReadyToast also owns the store's
 * event subscription + initial-state fetch; this component just reads the
 * store.
 */

import { useEffect, useState } from 'react';
import { X, ArrowDownToLine } from 'lucide-react';
import { Progress } from '../../../components/ui/progress';
import { useAutoUpdatesStore } from '../../../store/autoUpdatesStore';

/** Check if we're in a desktop (Electron) environment */
function isDesktop(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    navigator.userAgent.includes('Electron') &&
    !!window.electronAPI?.autoUpdates
  );
}

/** Format bytes to human-readable string */
function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

export function UpdateDownloadToast() {
  const { state } = useAutoUpdatesStore();
  const [isDismissed, setIsDismissed] = useState(false);

  const { status, updateInfo, downloadProgress } = state;

  // Reset dismissed state when a new download starts (store-derived — the
  // event subscription lives in UpdateReadyToast, the single owner).
  useEffect(() => {
    if (status === 'available') {
      setIsDismissed(false);
    }
  }, [status]);

  // Don't render in web builds
  if (!isDesktop()) return null;

  // Show for downloading and available (starting download) states only —
  // 'downloaded' is UpdateReadyPrompt's, never rendered here.
  const isDownloading = status === 'downloading' || status === 'available';

  if (!isDownloading || isDismissed) return null;

  const handleDismiss = () => {
    setIsDismissed(true);
  };

  // Render "Downloading" toast
  return (
    <div
      className="fixed bottom-4 right-4 z-50 w-80 rounded-lg border border-border bg-background shadow-lg p-4"
      data-testid="update-download-toast"
    >
      <div className="flex items-start gap-3">
        <ArrowDownToLine className="h-5 w-5 text-blue-600 dark:text-blue-400 animate-pulse mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-medium truncate">
              Downloading update
            </span>
            <button
              onClick={handleDismiss}
              className="rounded-md p-1 opacity-70 transition-opacity hover:opacity-100 hover:bg-muted"
              aria-label="Dismiss"
              data-testid="dismiss-download-toast"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <span className="text-xs text-muted-foreground block truncate">
            v{updateInfo?.version ?? 'unknown'}
          </span>
          <div className="mt-2 space-y-1">
            <Progress
              value={downloadProgress?.percent ?? 0}
              className="h-1.5"
              data-testid="toast-download-progress-bar"
            />
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span data-testid="toast-download-progress-text">
                {downloadProgress
                  ? `${formatBytes(downloadProgress.bytesTransferred)} / ${formatBytes(downloadProgress.totalBytes)}`
                  : 'Starting...'}
              </span>
              <span data-testid="toast-download-progress-percent">
                {downloadProgress?.percent?.toFixed(0) ?? 0}%
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
